import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save, Package } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProductSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [price, setPrice] = useState("");
  const [productUrl, setProductUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [description, setDescription] = useState("");
  const [features, setFeatures] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [discountText, setDiscountText] = useState("");

  useEffect(() => {
    fetchProduct();
  }, []);

  const fetchProduct = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      setLoading(true);
      const response = await axios.get(`${API}/product`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;
      setPrice(data.price || 249.90);
      setProductUrl(data.product_url || "");
      setImageUrl(data.image_url || "");
      setDescription(data.description || "");
      setFeatures(data.features || []);
      setIsActive(data.is_active !== false);
      setDiscountText(data.discount_text || "");
    } catch (error) {
      console.error("Error fetching product:", error);
      toast.error("Ürün bilgileri yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem("admin_token");

    if (!price || parseFloat(price) <= 0) {
      toast.error("Geçerli bir fiyat girin");
      return;
    }

    if (!productUrl) {
      toast.error("Ürün URL'si gerekli");
      return;
    }

    setSaving(true);

    try {
      await axios.put(
        `${API}/admin/product`,
        {
          price: parseFloat(price),
          product_url: productUrl,
          image_url: imageUrl || null,
          description: description,
          features: features.filter((f) => f.trim()),
          is_active: isActive,
          discount_text: discountText || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("Ürün ayarları güncellendi!");
    } catch (error) {
      console.error("Error updating product:", error);
      toast.error("Güncelleme başarısız");
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    setFeatures([...features, ""]);
  };

  const removeFeature = (index) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...features];
    newFeatures[index] = value;
    setFeatures(newFeatures);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-teal-400">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-product-settings">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-black text-gradient">Ürün Ayarları</h2>
        <div className="flex items-center gap-3">
          <Label className="text-teal-400 font-semibold">Satış Aktif</Label>
          <Switch
            checked={isActive}
            onCheckedChange={setIsActive}
            className="data-[state=checked]:bg-teal-500"
            data-testid="product-active-switch"
          />
        </div>
      </div>

      <div className="glass-card-premium rounded-2xl p-8 neon-border space-y-6">
        {/* Price */}
        <div className="space-y-3">
          <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">
            <Package className="w-4 h-4 inline mr-2" />
            Ürün Fiyatı (TL)
          </Label>
          <Input
            type="number"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="249.90"
            className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white text-lg"
            data-testid="product-price-input"
          />
        </div>

        {/* Product URL */}
        <div className="space-y-3">
          <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">
            E-Ticaret URL
          </Label>
          <Input
            type="url"
            value={productUrl}
            onChange={(e) => setProductUrl(e.target.value)}
            placeholder="https://alcodot.com/shop"
            className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white text-lg"
            data-testid="product-url-input"
          />
        </div>

        {/* Image URL */}
        <div className="space-y-3">
          <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">
            Ürün Görseli URL
          </Label>
          <Input
            type="url"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://cdn.alcodot.com/product.jpg"
            className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white text-lg"
            data-testid="product-image-input"
          />
          <p className="text-xs text-gray-500">
            Görsel URL'si (opsiyonel). Boş bırakılırsa varsayılan icon gösterilir.
          </p>
          
          {/* Image Preview */}
          {imageUrl && (
            <div className="mt-4 p-4 bg-gray-900/30 rounded-xl border border-teal-500/20">
              <p className="text-xs text-teal-400 font-bold uppercase mb-3">Görsel Önizleme</p>
              <div className="flex justify-center">
                <img 
                  src={imageUrl} 
                  alt="Ürün görseli" 
                  className="max-w-xs max-h-48 rounded-xl object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<p class="text-red-400 text-sm">Görsel yüklenemedi - URL kontrol edin</p>';
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Description */}
        <div className="space-y-3">
          <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">
            Ürün Açıklaması
          </Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Profesyonel alkol test cihazı..."
            rows={3}
            className="rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white"
            data-testid="product-description-input"
          />
        </div>

        {/* Discount Text */}
        <div className="space-y-3">
          <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">
            İndirim/Kampanya Metni (Opsiyonel)
          </Label>
          <Input
            type="text"
            value={discountText}
            onChange={(e) => setDiscountText(e.target.value)}
            placeholder="Örn: İlk alışverişe %10 indirim"
            className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white text-lg"
            data-testid="product-discount-input"
          />
        </div>

        {/* Features */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">
              Ürün Özellikleri
            </Label>
            <Button
              onClick={addFeature}
              size="sm"
              variant="outline"
              className="rounded-xl bg-teal-500/10 border-teal-500/50 text-teal-400 hover:bg-teal-500/20"
              data-testid="add-feature-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ekle
            </Button>
          </div>

          <div className="space-y-3">
            {features.map((feature, index) => (
              <div key={index} className="flex gap-3">
                <Input
                  value={feature}
                  onChange={(e) => updateFeature(index, e.target.value)}
                  placeholder="Örn: Anında sonuç"
                  className="h-12 rounded-xl bg-gray-900/50 border-2 border-teal-500/30 text-white"
                  data-testid={`feature-input-${index}`}
                />
                <Button
                  onClick={() => removeFeature(index)}
                  variant="ghost"
                  size="sm"
                  className="h-12 w-12 p-0 text-red-400 hover:bg-red-500/10 rounded-xl"
                  data-testid={`remove-feature-${index}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full h-14 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-400 hover:to-cyan-500 text-black font-black text-lg shadow-lg neon-glow"
          data-testid="save-product-btn"
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? "Kaydediliyor..." : "Ayarları Kaydet"}
        </Button>
      </div>
    </div>
  );
};

export default AdminProductSettings;